import type { ProbeConnection, OptionBytes, FaultRegisters } from './types';
import { formatHexDump } from '@/utils/hex';

// ============================================================================
// MCU Reader — Flash readback, option bytes, fault diagnosis (spec section 6.4)
// ============================================================================

/**
 * MCU Reader provides high-level readback operations.
 */
export class MCUReader {
  private probe: ProbeConnection;

  constructor(probe: ProbeConnection) {
    this.probe = probe;
  }

  /**
   * Read the entire flash contents.
   */
  async readFlash(onProgress?: (percent: number) => void): Promise<Uint8Array> {
    return this.probe.readFlash(onProgress);
  }

  /**
   * Read a specific memory region.
   */
  async readRegion(address: number, length: number): Promise<Uint8Array> {
    return this.probe.readRegion(address, length);
  }

  /**
   * Read option bytes (RDP level, write protection, etc.).
   */
  async readOptionBytes(): Promise<OptionBytes> {
    return this.probe.readOptionBytes();
  }

  /**
   * Read fault status registers for HardFault diagnosis.
   */
  async readFaultRegisters(): Promise<FaultRegisters> {
    return this.probe.readFaultRegisters();
  }

  /**
   * Check if the target already has firmware programmed.
   * Reads the first 4 bytes at the flash base — if they're all 0xFF, flash is blank.
   */
  async isAlreadyProgrammed(flashBase: number = 0x08000000): Promise<boolean> {
    const header = await this.probe.readMemory(flashBase, 4);
    return !(header[0] === 0xFF && header[1] === 0xFF && header[2] === 0xFF && header[3] === 0xFF);
  }

  /**
   * Download readback data as a .bin file.
   */
  downloadAsBin(data: Uint8Array, filename: string = 'readback.bin'): void {
    const blob = new Blob([new Uint8Array(data)], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Format readback data as a hex dump string.
   */
  formatAsHexDump(data: Uint8Array, baseAddress: number = 0): string {
    return formatHexDump(data, baseAddress);
  }
}
